import { Link } from 'react-router-dom';

import { Button, StatusNotice } from '@/components/ui';
import { cx, focusRingClass } from '@/components/ui/shared';
import {
  consoleCanonicalOwnerLabels,
  getConsoleRouteById,
  getConsoleRouteGroup,
  getConsoleRouteGroupLabel,
  type ConsoleRouteLink,
} from '@/navigation/consoleNavigation';
import { getDeviceWorkspacePath } from '@/navigation/consoleRoutePaths';
import { describeDeviceConnection } from '@/utils/deviceConnection';

import type { DeviceSearchResult } from './consoleDeviceSearch';

/** Every result link carries this attribute so the arrow keys can move between them. */
export const SEARCH_RESULT_SELECTOR = '[data-console-search-result]';

const DEVICES_HEADING_ID = 'console-search-devices';
const PAGES_HEADING_ID = 'console-search-pages';

const resultLinkClass = cx(
  focusRingClass,
  'grid min-h-control-comfortable grid-cols-[auto_minmax(0,1fr)] gap-3 rounded-control border border-border-subtle bg-surface p-3 text-ink hover:border-accent hover:bg-accent-soft',
);

interface DeviceSearchResultsProps {
  result: DeviceSearchResult;
  isLoading: boolean;
  isRetrying: boolean;
  failed: boolean;
  onRetry: () => void;
  onNavigate: () => void;
}

/** Matching devices with their connection and stage, each opening its device page. */
export function DeviceSearchResults({
  result,
  isLoading,
  isRetrying,
  failed,
  onRetry,
  onNavigate,
}: DeviceSearchResultsProps) {
  const DeviceIcon = getConsoleRouteById('devices').icon;

  return (
    <section aria-labelledby={DEVICES_HEADING_ID} className="grid gap-2">
      <h3 id={DEVICES_HEADING_ID} className="text-meta font-semibold text-ink-muted">
        Devices
      </h3>
      {isLoading ? (
        <p className="text-body text-ink-muted">Looking for devices…</p>
      ) : failed ? (
        <StatusNotice
          state="failed"
          title="Devices can’t be searched right now"
          description="SoundOps could not load this organization’s devices. Pages can still be found below."
          action={
            <Button type="button" size="sm" variant="secondary" isLoading={isRetrying} onClick={onRetry}>
              Try again
            </Button>
          }
        />
      ) : (
        <>
          <ul className="grid gap-2" aria-labelledby={DEVICES_HEADING_ID}>
            {result.matches.map((record) => {
              const { device } = record;
              return (
                <li key={device.device_id}>
                  <Link
                    to={getDeviceWorkspacePath(device.device_id)}
                    onClick={onNavigate}
                    className={resultLinkClass}
                    data-console-search-result=""
                  >
                    <DeviceIcon className="mt-0.5 h-5 w-5 text-accent" aria-hidden="true" />
                    <span className="min-w-0">
                      <span className="block break-words text-control font-semibold text-ink-strong">
                        {device.device_name}
                      </span>
                      <span className="mt-1 block text-meta text-ink-muted">
                        <span className="so-data-text">#{device.device_id}</span> ·{' '}
                        {describeDeviceConnection(record).label} · {record.monitoring.label}
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
          {result.total > result.matches.length ? (
            <p className="text-meta text-ink-muted">
              Showing {result.matches.length} of {result.total} devices. Type more of the name
              to narrow the list.
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

interface PageSearchResultsProps {
  routes: readonly ConsoleRouteLink[];
  onNavigate: () => void;
}

/** Matching pages this person can open. */
export function PageSearchResults({ routes, onNavigate }: PageSearchResultsProps) {
  return (
    <section aria-labelledby={PAGES_HEADING_ID} className="grid gap-2">
      <h3 id={PAGES_HEADING_ID} className="text-meta font-semibold text-ink-muted">
        Pages
      </h3>
      <ul className="grid gap-2" aria-labelledby={PAGES_HEADING_ID}>
        {routes.map((route) => {
          const Icon = route.icon;
          const groupLabel = getConsoleRouteGroupLabel(getConsoleRouteGroup(route.groupId));
          return (
            <li key={route.id}>
              <Link
                to={route.path}
                onClick={onNavigate}
                className={resultLinkClass}
                data-console-search-result=""
              >
                <Icon className="mt-0.5 h-5 w-5 text-accent" aria-hidden="true" />
                <span className="min-w-0">
                  <span className="block text-control font-semibold text-ink-strong">
                    {route.label}
                  </span>
                  <span className="mt-1 block text-meta text-ink-muted">
                    {groupLabel} · {consoleCanonicalOwnerLabels[route.canonicalOwnerId]}
                  </span>
                  <span className="mt-1 block text-body">{route.description}</span>
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
